import type { StringObject } from "../../objects/StringObject";
import { styleTxn } from "../style-txn";
import { CursorOffsetCalculator } from "./CursorOffsetCalculator";

const DEFAULT_CURSOR_ID = "default";
const CURSOR_PORTAL_SELECTOR = "[string-cursor],[data-string-cursor]";
const CURSOR_PORTAL_CONTENT_SELECTOR = "[string-cursor-content],[data-string-cursor-content]";
const CURSOR_EPSILON = 0.0005;

const roundTo = (value: number, precision: number) => {
  const scale = Math.pow(10, precision);
  return Math.round(value * scale) / scale;
};

type CursorPortalPrevSnapshot = {
  x: number;
  y: number;
  stepX: number;
  stepY: number;
};

type CursorPortal = {
  id: string;
  element: HTMLElement;
  content: HTMLElement | null;
  prev: CursorPortalPrevSnapshot;
  hoverCount: number;
  showTimer: ReturnType<typeof setTimeout> | null;
  lerp: number | null;
};

/**
 * `[string-cursor]` portals: elements elsewhere in the DOM (outside a hovered
 * object) that mirror the cursor's position via `--x`/`--y` CSS vars, at their
 * own lerp speed, gated by hover count so multiple hovered objects sharing a
 * portal id don't fight over its show/hide state.
 */
export class CursorPortalManager {
  private portals: Map<string, CursorPortal[]> = new Map();

  constructor(
    private readonly resolveCustomLerpSetting: () => string | number | undefined,
    private readonly adaptiveLerp: (input: {
      value: number;
      inMin: number;
      inMax: number;
      outMin: number;
      outMax: number;
    }) => number,
  ) {}

  hasPortals(): boolean {
    return this.portals.size > 0;
  }

  collect(targetX: number, targetY: number): void {
    this.portals.clear();
    const nodes = document.querySelectorAll(CURSOR_PORTAL_SELECTOR);
    nodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const id = this.resolvePortalId(node);
      const customLerp = this.resolvePortalLerp(node);
      const content = node.matches(CURSOR_PORTAL_CONTENT_SELECTOR)
        ? node
        : (node.querySelector(CURSOR_PORTAL_CONTENT_SELECTOR) as HTMLElement | null);

      const portal: CursorPortal = {
        id,
        element: node,
        content,
        prev: { x: targetX, y: targetY, stepX: 0, stepY: 0 },
        hoverCount: 0,
        showTimer: null,
        lerp: customLerp,
      };

      const existing = this.portals.get(id);
      if (existing) {
        existing.push(portal);
      } else {
        this.portals.set(id, [portal]);
      }
    });
  }

  shouldRefresh(nodes: NodeList): boolean {
    for (const node of Array.from(nodes)) {
      if (!(node instanceof Element)) continue;
      if (node.matches(CURSOR_PORTAL_SELECTOR)) {
        return true;
      }
      if (node.querySelector(CURSOR_PORTAL_SELECTOR)) {
        return true;
      }
    }
    return false;
  }

  updateAll(targetX: number, targetY: number, dt: number): void {
    this.portals.forEach((bucket) => {
      bucket.forEach((portal) => {
        this.updatePosition(portal, targetX, targetY, dt);
      });
    });
  }

  withPortalsForObject(object: StringObject | null, cb: (portal: CursorPortal) => void): void {
    this.getPortalsForObject(object).forEach((portal) => cb(portal));
  }

  onObjectHoverStart(object: StringObject | null, cursorClass: string | null): void {
    this.withPortalsForObject(object, (portal) => {
      if (cursorClass) {
        portal.element.classList.add(cursorClass);
      }
      this.incrementHover(portal);
    });
  }

  onObjectHoverEnd(object: StringObject | null, cursorClass: string | null): void {
    this.withPortalsForObject(object, (portal) => {
      if (cursorClass) {
        portal.element.classList.remove(cursorClass);
      }
      this.decrementHover(portal);
    });
  }

  private resolvePortalId(element: HTMLElement): string {
    const attrSources = [
      element.getAttribute("data-string-cursor"),
      element.getAttribute("string-cursor"),
      element.getAttribute("data-string-cursor-id"),
      element.getAttribute("string-cursor-id"),
    ];

    for (const source of attrSources) {
      if (source && source.trim().length > 0) {
        return source.trim();
      }
    }

    return DEFAULT_CURSOR_ID;
  }

  private resolvePortalLerp(element: HTMLElement): number | null {
    const raw =
      element.getAttribute("data-string-cursor-lerp") ??
      element.getAttribute("string-cursor-lerp") ??
      this.resolveCustomLerpSetting();
    if (!raw) {
      return null;
    }

    const value = parseFloat(String(raw));
    if (!Number.isFinite(value)) {
      return null;
    }

    const clamped = Math.min(1, Math.max(0.01, value));
    return this.adaptiveLerp({
      value: clamped,
      inMin: 0.1,
      inMax: 1.0,
      outMin: 0.05,
      outMax: 0.65,
    });
  }

  private getPortalsForObject(object: StringObject | null): CursorPortal[] {
    if (this.portals.size === 0) {
      return [];
    }

    const ids = this.extractPortalIds(object);
    const selected: CursorPortal[] = [];

    ids.forEach((id) => {
      if (id === "*") {
        this.portals.forEach((bucket) => {
          bucket.forEach((portal) => selected.push(portal));
        });
        return;
      }

      const normalized = id.length > 0 ? id : DEFAULT_CURSOR_ID;
      const portals = this.portals.get(normalized);
      if (portals) {
        portals.forEach((portal) => selected.push(portal));
      }
    });

    if (selected.length === 0) {
      const fallbackBucket = this.portals.get(DEFAULT_CURSOR_ID) ?? this.portals.values().next().value;
      if (fallbackBucket && fallbackBucket.length > 0) {
        fallbackBucket.forEach((portal) => selected.push(portal));
      }
    }

    return selected;
  }

  private extractPortalIds(object: StringObject | null): string[] {
    if (!object) return [DEFAULT_CURSOR_ID];
    const raw = object.getProperty<string>("cursor-target");
    if (typeof raw !== "string" || raw.trim().length === 0) {
      return [DEFAULT_CURSOR_ID];
    }

    return raw
      .split(/[,|]/)
      .map((value) => value.trim())
      .filter(Boolean);
  }

  private incrementHover(portal: CursorPortal): void {
    portal.hoverCount++;
    portal.element.classList.remove("-show");
    this.restartShowTimer(portal);
  }

  private decrementHover(portal: CursorPortal): void {
    portal.hoverCount = Math.max(0, portal.hoverCount - 1);
    if (portal.hoverCount === 0) {
      this.clearShowTimer(portal);
      portal.element.classList.remove("-show");
    }
  }

  private restartShowTimer(portal: CursorPortal): void {
    this.clearShowTimer(portal);
    if (!portal.element.isConnected) {
      portal.showTimer = null;
      return;
    }
    portal.element.classList.add("-show");
    portal.showTimer = null;
  }

  private clearShowTimer(portal: CursorPortal): void {
    if (portal.showTimer) {
      clearTimeout(portal.showTimer);
      portal.showTimer = null;
    }
  }

  private updatePosition(portal: CursorPortal, targetX: number, targetY: number, dt: number): void {
    if (!portal.element.isConnected) return;

    const prev = portal.prev;

    const currentX = Number.isFinite(prev.x) ? prev.x : targetX;
    const currentY = Number.isFinite(prev.y) ? prev.y : targetY;

    const factor = portal.lerp ?? 0.1;
    const fpsAdjustedLerp = CursorOffsetCalculator.getFrameAdjustedLerp(factor, dt);

    const stepX = (targetX - currentX) * fpsAdjustedLerp;
    const stepY = (targetY - currentY) * fpsAdjustedLerp;

    const safeDt = dt > 1e-4 ? dt : 1 / 60;
    const normalizedVelocityX = stepX / (safeDt * 60);
    const normalizedVelocityY = stepY / (safeDt * 60);

    if (Math.abs(stepX) < CURSOR_EPSILON && Math.abs(stepY) < CURSOR_EPSILON) {
      return;
    }

    const nextX = currentX + stepX;
    const nextY = currentY + stepY;

    this.writeVars(portal.element, {
      "--x": roundTo(nextX, 2),
      "--y": roundTo(nextY, 2),
      "--x-lerp": roundTo(normalizedVelocityX, 3),
      "--y-lerp": roundTo(normalizedVelocityY, 3),
    });

    prev.x = nextX;
    prev.y = nextY;
    prev.stepX = stepX;
    prev.stepY = stepY;
  }

  private writeVars(el: HTMLElement, vars: Record<string, number>): void {
    if (styleTxn.isOpen) {
      styleTxn.setVars(el, vars);
      return;
    }
    styleTxn.run(() => {
      styleTxn.setVars(el, vars);
    });
  }
}
